import { memo, useEffect, useRef, useState } from 'react'
import type { GymLeaderboardEntry, GymLeaderboardMetric } from '@gymboard/types'
import { BOARD_METRICS, type GymBoards } from '../../hooks/useGymBoards'
import './gym-leaderboard.css'

const ROWS = 8
/** How long each metric holds the board. */
const DWELL_MS = 20_000
/** Values blur out this long before the board re-sorts to the next metric. */
const SWAP_OUT_MS = 180
/** Leaving rows stay mounted this long so they can fade (see .gb-row.out). */
const LEAVE_MS = 300
const STAGGER_MS = 50
const COUNT_MS = 900

/** Podium colours: camel, wool-silver, bronze (same as the in-class chorus). */
const PODIUM = ['#D9B68A', '#B4B3C2', '#C98B5A']
const PODIUM_BAR = ['rgba(217,182,138,0.16)', 'rgba(180,179,194,0.12)', 'rgba(201,139,90,0.14)']

const TITLES: Record<GymLeaderboardMetric, string> = {
  visits: 'Most days at the gym',
  classes: 'Most classes',
  minutes: 'Most active minutes',
  calories: 'Most calories burned',
}
const TABS: Record<GymLeaderboardMetric, string> = {
  visits: 'Visits',
  classes: 'Classes',
  minutes: 'Minutes',
  calories: 'Calories',
}

function unit(metric: GymLeaderboardMetric, value: number) {
  if (metric === 'visits') return value === 1 ? 'day' : 'days'
  if (metric === 'classes') return value === 1 ? 'class' : 'classes'
  return metric === 'minutes' ? 'min' : 'kcal'
}

function initials(name: string) {
  const parts = name.replace(/\./g, '').trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
}

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Counts toward a new value on a data refresh; jumps when the metric changes
 *  (that change happens under the swap blur, so a count would read as noise). */
function CountUp({ value, metric }: { value: number; metric: GymLeaderboardMetric }) {
  const [shown, setShown] = useState(value)
  const current = useRef(value)
  const lastMetric = useRef(metric)

  useEffect(() => {
    if (metric !== lastMetric.current || reducedMotion()) {
      lastMetric.current = metric
      current.current = value
      setShown(value)
      return
    }
    const from = current.current
    if (from === value) return
    const t0 = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / COUNT_MS)
      const v = from + (value - from) * (1 - Math.pow(1 - p, 3))
      current.current = v
      setShown(v)
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, metric])

  return <>{Math.round(shown).toLocaleString('en-US')}</>
}

function Avatar({ name, url }: { name: string; url: string | null }) {
  const [broken, setBroken] = useState(false)
  if (url && !broken) return <img className="gb-av" src={url} alt="" onError={() => setBroken(true)} />
  return <span className="gb-av" aria-hidden>{initials(name)}</span>
}

const Row = memo(function Row({
  entry,
  index,
  rowPx,
  leaving,
  ratio,
  metric,
  delayMs,
}: {
  entry: GymLeaderboardEntry
  index: number
  rowPx: number
  leaving: boolean
  /** value / top value, drives the bar behind the row. */
  ratio: number
  metric: GymLeaderboardMetric
  /** Entrance stagger; only the board's first appearance uses it. */
  delayMs: number
}) {
  // Mount at opacity 0 a little below the slot, then go to the slot on the
  // next frame so the transition runs (old TV browsers lack @starting-style).
  const [mounted, setMounted] = useState(false)
  const [delay, setDelay] = useState(delayMs)
  useEffect(() => {
    let b = 0
    const a = requestAnimationFrame(() => {
      b = requestAnimationFrame(() => setMounted(true))
    })
    // Drop the stagger once the entrance is done, or every later rank change
    // would wait for it too.
    const t = delay ? window.setTimeout(() => setDelay(0), delay + 800) : 0
    return () => {
      cancelAnimationFrame(a)
      cancelAnimationFrame(b)
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const y = index * rowPx + (mounted ? 0 : rowPx * 0.3)
  const podium = entry.rank <= 3 ? entry.rank - 1 : -1
  const font = Math.max(14, Math.min(rowPx * 0.36, 34))

  return (
    <div
      className={`gb-row${mounted && !leaving ? ' in' : ''}${leaving ? ' out' : ''}`}
      style={{
        height: rowPx,
        fontSize: font,
        transform: `translate3d(0, ${y}px, 0)`,
        transitionDelay: delay ? `${delay}ms` : undefined,
      }}
      data-athlete-id={entry.athleteId}
    >
      <div
        className="gb-bar"
        style={{
          transform: `scaleX(${mounted ? ratio : 0})`,
          background: podium >= 0 ? PODIUM_BAR[podium] : undefined,
          transitionDelay: delay ? `${delay}ms` : undefined,
        }}
      />
      <span className="gb-rk" style={podium >= 0 ? { color: PODIUM[podium] } : undefined}>
        {entry.rank}
      </span>
      <Avatar name={entry.displayName} url={entry.avatarUrl} />
      <span className="gb-nm">{entry.displayName}</span>
      <span className="gb-val">
        <CountUp value={entry.value} metric={metric} />
        <small>{unit(metric, entry.value)}</small>
      </span>
    </div>
  )
})

interface Slot {
  entry: GymLeaderboardEntry
  index: number
  leaving: boolean
  /** The metric this row's value belongs to; a leaving row keeps its own. */
  metric: GymLeaderboardMetric
  /** value / that board's top value. */
  ratio: number
}

interface Props {
  boards: GymBoards
  /** Rotation order; boards with nobody on them are skipped. */
  metrics?: GymLeaderboardMetric[]
}

/**
 * This week's gym leaderboard for the idle TV. Rotates through the metrics
 * (visits first when the gym has floor check-in on, then classes, minutes
 * and calories); rows keep their DOM node and travel to their new rank
 * (transform only), so a re-sort reads as movement.
 */
export default function GymLeaderboard({ boards, metrics = BOARD_METRICS }: Props) {
  const available = metrics.filter((m) => (boards[m]?.entries.length ?? 0) > 0)
  const availableKey = available.join(',')

  const [metric, setMetric] = useState<GymLeaderboardMetric | null>(available[0] ?? null)
  const [swapping, setSwapping] = useState(false)
  const [cycle, setCycle] = useState(0)

  useEffect(() => {
    if (available.length && (!metric || !available.includes(metric))) setMetric(available[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableKey, metric])

  useEffect(() => {
    if (!metric || available.length < 2) return
    let swapTimer = 0
    const dwell = window.setTimeout(() => {
      setSwapping(true)
      swapTimer = window.setTimeout(() => {
        const i = available.indexOf(metric)
        setMetric(available[(i + 1) % available.length])
        setSwapping(false)
        setCycle((c) => c + 1)
      }, SWAP_OUT_MS)
    }, DWELL_MS)
    return () => {
      clearTimeout(dwell)
      if (swapTimer) {
        clearTimeout(swapTimer)
        setSwapping(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, availableKey])

  const board = metric ? boards[metric] : undefined
  const entries = (board?.entries ?? []).slice(0, ROWS)
  const max = entries[0]?.value ?? 0

  // Current rows come straight from this render's data, so a metric switch
  // changes values and metric together (CountUp jumps instead of counting).
  // Rows that drop off stay, marked leaving, long enough to fade.
  const current: Slot[] = metric
    ? entries.map((entry, index) => ({ entry, index, leaving: false, metric, ratio: max > 0 ? entry.value / max : 0 }))
    : []
  const signature = `${metric}|${entries.map((e) => `${e.athleteId}:${e.value}`).join()}`
  const [prev, setPrev] = useState<{ signature: string; rows: Slot[] }>({ signature, rows: current })
  const [leaving, setLeaving] = useState<Slot[]>([])
  if (prev.signature !== signature) {
    const ids = new Set(current.map((s) => s.entry.athleteId))
    const dropped = prev.rows.filter((s) => !ids.has(s.entry.athleteId)).map((s) => ({ ...s, leaving: true }))
    setLeaving([...leaving.filter((s) => !ids.has(s.entry.athleteId)), ...dropped])
    setPrev({ signature, rows: current })
  }
  useEffect(() => {
    if (!leaving.length) return
    const t = window.setTimeout(() => setLeaving([]), LEAVE_MS)
    return () => clearTimeout(t)
  }, [leaving])
  const currentIds = new Set(current.map((s) => s.entry.athleteId))
  const slots = [...current, ...leaving.filter((s) => !currentIds.has(s.entry.athleteId))]

  // Only the board's first appearance staggers in.
  const [firstPaint, setFirstPaint] = useState(true)
  useEffect(() => {
    if (!firstPaint || slots.length === 0) return
    const t = window.setTimeout(() => setFirstPaint(false), ROWS * STAGGER_MS + 800)
    return () => clearTimeout(t)
  }, [firstPaint, slots.length])

  const listRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const measure = () => setHeight(el.clientHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const rowPx = height / ROWS

  // Stable DOM order (by id) so a rank change never re-mounts a row.
  const ordered = [...slots].sort((a, b) => (a.entry.athleteId < b.entry.athleteId ? -1 : 1))
  const total = board?.totalParticipants ?? 0

  if (!metric) return null

  return (
    <section className={`gb${swapping ? ' gb-swap' : ''}`} aria-label="Gym leaderboard this week">
      <div className="gb-head">
        <div>
          <div className="gb-eyebrow">This week</div>
          <h2 className="gb-title">{TITLES[metric]}</h2>
        </div>
        {available.length > 1 && (
          <div className="gb-tabs" aria-hidden>
            {available.map((m) => (
              <span key={m} className={m === metric ? 'on' : undefined}>
                {TABS[m]}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="gb-track" aria-hidden>
        {available.length > 1 && (
          <i key={`${metric}-${cycle}`} style={{ animationDuration: `${DWELL_MS + SWAP_OUT_MS}ms` }} />
        )}
      </div>

      <div className="gb-list" ref={listRef}>
        {rowPx > 0 &&
          ordered.map((s) => (
            <Row
              key={s.entry.athleteId}
              entry={s.entry}
              index={s.index}
              rowPx={rowPx}
              leaving={s.leaving}
              ratio={s.ratio}
              metric={s.metric}
              delayMs={firstPaint ? s.index * STAGGER_MS : 0}
            />
          ))}
      </div>

      <div className="gb-foot">
        {total} {total === 1 ? 'member' : 'members'} trained this week
      </div>
    </section>
  )
}
